// Auth API — uses api.intrlock.io/auth (v2 CDK-deployed Lambda in us-east-1)
const AUTH_BASE = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';

/** Stable per-browser device ID — used for the "trust this device" 30-day TOTP skip. */
export function getOrCreateDeviceId(): string {
  let id = localStorage.getItem('intrlock_device_id');
  if (!id) {
    id = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem('intrlock_device_id', id);
  }
  return id;
}

export type LoginResponse =
  | { totp_required: true; totp_token: string }
  | {
      totp_required?: false;
      token: string;
      totp_enrollment_required?: boolean;
      user: any;
    };

export const loginApi = async (email: string, password: string): Promise<LoginResponse> => {
  const res = await fetch(`${AUTH_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, device_id: getOrCreateDeviceId() }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.message || 'Invalid email or password');
  return data;
};

/** Step 2 of login when TOTP is enabled. */
export const totpVerifyApi = async (totpToken: string, code: string, trustDevice: boolean) => {
  const res = await fetch(`${AUTH_BASE}/auth/totp/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      totp_token: totpToken,
      code,
      device_id: getOrCreateDeviceId(),
      trust_device: trustDevice,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.message || 'Invalid code');
  return data;
};

/** Self-signup from landing page. */
export const signupApi = async (input: {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  company?: string;
  phone?: string;
  role_hint?: string;
}) => {
  const res = await fetch(`${AUTH_BASE}/auth/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.message || 'Could not create account');
  return data;
};

export class SessionValidationError extends Error {
  constructor(public readonly status: number) {
    super(`Session validation failed (${status})`);
  }
}

export const meApi = async (tokenOverride?: string) => {
  const token = tokenOverride ?? localStorage.getItem('intrlock_token');
  const res = await fetch(`${AUTH_BASE}/auth/me`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) throw new SessionValidationError(res.status);
  return res.json();
};

// ── TOTP self-service ───────────────────────────────────────────────────────

function authHeaders() {
  const token = localStorage.getItem('intrlock_token') || '';
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export const totpStatusApi = async () => {
  const res = await fetch(`${AUTH_BASE}/auth/totp/status`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Could not load TOTP status');
  return res.json() as Promise<{
    enabled: boolean;
    enabled_at: string | null;
    backup_codes_remaining: number;
    trusted_devices_count: number;
    enrollment_required: boolean;
  }>;
};

export const totpEnrollStartApi = async () => {
  const res = await fetch(`${AUTH_BASE}/auth/totp/enroll-start`, { method: 'POST', headers: authHeaders() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Could not start enrollment');
  return data as { secret: string; otpauth_uri: string };
};

export const totpEnrollVerifyApi = async (code: string) => {
  const res = await fetch(`${AUTH_BASE}/auth/totp/enroll-verify`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify({ code }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Invalid code');
  return data as { enabled: true; backup_codes: string[] };
};

export const totpDisableApi = async (code: string) => {
  const res = await fetch(`${AUTH_BASE}/auth/totp/disable`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify({ code }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Invalid code');
  return data;
};

export const totpRegenBackupCodesApi = async (code: string) => {
  const res = await fetch(`${AUTH_BASE}/auth/totp/regen-backup-codes`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify({ code }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Invalid code');
  return data as { backup_codes: string[] };
};

export const totpAdminResetApi = async (userId: string) => {
  const res = await fetch(`${AUTH_BASE}/auth/totp/admin-reset`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify({ user_id: userId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Could not reset');
  return data;
};

export const logoutApi = async () => {
  // Stateless JWT — just clear local storage (handled by AuthContext)
};
